import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, Funnel, ArrowLeft } from "@phosphor-icons/react";

const ACTION_COLORS = {
  add: "bg-green-600/20 text-green-500",
  take_out: "bg-blue-600/20 text-blue-400",
  packs: "bg-purple-600/20 text-purple-400",
  physical_count: "bg-yellow-500/20 text-yellow-500",
  transfer: "bg-cyan-600/20 text-cyan-400",
  transfer_receive: "bg-cyan-600/20 text-cyan-400",
  reversal: "bg-destructive/20 text-destructive",
};

export default function InventoryTransactions() {
  const [tx, setTx] = useState([]);
  const [drygTx, setDrygTx] = useState([]);
  const [tab, setTab] = useState("meals");
  const [filter, setFilter] = useState({ action: "", diet_type: "", limit: 200 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.action) params.set("action", filter.action);
      if (filter.diet_type) params.set("diet_type", filter.diet_type);
      params.set("limit", filter.limit);
      const [mealRes, dryRes] = await Promise.all([
        api.get(`/meals/transactions/list?${params.toString()}`),
        api.get(`/inventory/transactions?limit=200`),
      ]);
      setTx(mealRes.data);
      setDrygTx(dryRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter.action, filter.diet_type, filter.limit]);

  useEffect(() => { load(); }, [load]);

  const DIET_TYPES = ["Wellness", "Vegetarian", "Bland", "ESR", "CKD3", "Pescatarian", "GDM", "Well+"];
  const activeList = tab === "meals" ? tx : drygTx;

  return (
    <div className="p-4 md:p-6 max-w-7xl">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mb-3">
        <ArrowLeft size={14} /> Dashboard
      </Link>
      <div className="mb-4">
        <div className="label-tech mb-1">History</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Inventory Transactions</h1>
        <p className="text-sm text-muted-foreground mt-1 font-mono">
          {tab === "meals" ? `${tx.length} meal transactions` : `${drygTx.length} dry-goods transactions`}
        </p>
      </div>

      <div className="card-flat border-border p-3 mb-4 flex flex-wrap items-center gap-2">
        <button onClick={() => setTab("meals")} className={`h-8 px-3 border rounded-sm text-[10px] font-bold uppercase tracking-widest ${tab === "meals" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>Meals</button>
        <button onClick={() => setTab("dry")} className={`h-8 px-3 border rounded-sm text-[10px] font-bold uppercase tracking-widest ${tab === "dry" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>Dry Goods</button>
        {tab === "meals" && (
          <>
            <select value={filter.action} onChange={(e) => setFilter({ ...filter, action: e.target.value })} className="h-8 px-2 border border-border bg-background rounded-sm text-xs">
              <option value="">All actions</option>
              <option value="add">Add</option>
              <option value="take_out">Take Out</option>
              <option value="packs">Packs</option>
              <option value="physical_count">Physical Count</option>
              <option value="transfer">Transfer</option>
              <option value="reversal">Reversal</option>
            </select>
            <select value={filter.diet_type} onChange={(e) => setFilter({ ...filter, diet_type: e.target.value })} className="h-8 px-2 border border-border bg-background rounded-sm text-xs">
              <option value="">All diet types</option>
              {DIET_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </>
        )}
        <select value={filter.limit} onChange={(e) => setFilter({ ...filter, limit: e.target.value })} className="h-8 px-2 border border-border bg-background rounded-sm text-xs">
          {[50, 100, 200, 500].map((n) => <option key={n} value={n}>Last {n}</option>)}
        </select>
      </div>

      <div className="card-flat border-border overflow-hidden">
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full text-xs font-mono">
            <thead className="bg-muted/60 border-b border-border sticky top-0 z-10">
              <tr className="text-left label-tech">
                <th className="px-2 py-1.5">Time</th>
                <th className="px-2 py-1.5">Action</th>
                {tab === "meals" ? (
                  <>
                    <th className="px-2 py-1.5">Diet</th>
                    <th className="px-2 py-1.5">Code</th>
                    <th className="px-2 py-1.5">Unit</th>
                    <th className="px-2 py-1.5 text-right">Qty</th>
                    <th className="px-2 py-1.5 text-right">Δ meals</th>
                    <th className="px-2 py-1.5 text-right">Prev</th>
                    <th className="px-2 py-1.5 text-right">New</th>
                  </>
                ) : (
                  <>
                    <th className="px-2 py-1.5">Variant</th>
                    <th className="px-2 py-1.5">Location</th>
                    <th className="px-2 py-1.5 text-right">Prev</th>
                    <th className="px-2 py-1.5 text-right">New</th>
                    <th className="px-2 py-1.5 text-right">Δ</th>
                  </>
                )}
                <th className="px-2 py-1.5">User</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {activeList.map((t) => (
                <tr key={t.transaction_id} className="hover:bg-muted/30">
                  <td className="px-2 py-1 whitespace-nowrap">{new Date(t.timestamp).toLocaleString()}</td>
                  <td className="px-2 py-1"><span className={`px-1.5 py-0.5 rounded-sm ${ACTION_COLORS[t.action || t.kind] || "bg-muted"}`}>{t.action || t.kind}</span></td>
                  {tab === "meals" ? (
                    <>
                      <td className="px-2 py-1">{t.diet_type}</td>
                      <td className="px-2 py-1">{t.diet_code}</td>
                      <td className="px-2 py-1">{t.count_unit}</td>
                      <td className="px-2 py-1 text-right">{t.count_quantity}</td>
                      <td className={`px-2 py-1 text-right font-bold ${t.delta_meals >= 0 ? "text-success" : "text-destructive"}`}>{(t.delta_meals ?? 0).toFixed(0)}</td>
                      <td className="px-2 py-1 text-right">{(t.previous_qty ?? 0).toFixed(0)}</td>
                      <td className="px-2 py-1 text-right">{(t.new_qty ?? 0).toFixed(0)}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-2 py-1 truncate max-w-[20ch]">{t.variant_id}</td>
                      <td className="px-2 py-1">{t.location_id}</td>
                      <td className="px-2 py-1 text-right">{(t.previous_qty ?? 0).toFixed(0)}</td>
                      <td className="px-2 py-1 text-right">{(t.new_qty ?? 0).toFixed(0)}</td>
                      <td className={`px-2 py-1 text-right ${t.delta >= 0 ? "text-success" : "text-destructive"}`}>{(t.delta ?? 0).toFixed(0)}</td>
                    </>
                  )}
                  <td className="px-2 py-1">{t.user_email}</td>
                </tr>
              ))}
              {activeList.length === 0 && !loading && (
                <tr><td colSpan={10} className="p-6 text-center text-muted-foreground">No transactions.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
